import type { AdminEmailDateRange, AdminEmailMailboxCount } from "../models/admin-email.models";
import { ADMIN_EMAIL_DAY_MS, ADMIN_EMAIL_MAX_RANGE_DAYS } from "./admin-email.constants";

// Prefer an inbox with a successful provider count on first opening the page.
// An unread-count failure does not prove message reads will fail, so every supplied inbox stays selectable.
export const getAdminEmailInitialMailbox = (mailboxes: AdminEmailMailboxCount[]): AdminEmailMailboxCount | undefined =>
  mailboxes.find((mailbox) => !mailbox.error && mailbox.unreadCount !== null) ?? mailboxes[0];

// === EMAIL ===

// Keep chart dates explicit in UTC so mailbox totals do not change with the browser's timezone.
export const getAdminEmailDefaultDateRange = (): AdminEmailDateRange => {
  const today = new Date();
  const to = today.toISOString().slice(0, 10);
  const from = new Date(Date.parse(`${to}T00:00:00Z`) - 29 * ADMIN_EMAIL_DAY_MS).toISOString().slice(0, 10);

  return { from, to };
};

// === EMAIL ===

export const getAdminEmailDateRangeError = ({ from, to }: AdminEmailDateRange): string | null => {
  const fromTime = Date.parse(`${from}T00:00:00Z`);
  const toTime = Date.parse(`${to}T00:00:00Z`);

  if (!from || !to || !Number.isFinite(fromTime) || !Number.isFinite(toTime)) {
    return "Choose a start date and an end date.";
  }

  if (fromTime > toTime) {
    return "The start date must be on or before the end date.";
  }

  if ((toTime - fromTime) / ADMIN_EMAIL_DAY_MS + 1 > ADMIN_EMAIL_MAX_RANGE_DAYS) {
    return `Choose no more than ${ADMIN_EMAIL_MAX_RANGE_DAYS} days.`;
  }

  return null;
};

// === EMAIL ===

export const formatAdminEmailDate = (value: string): string => {
  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? "Unknown date" : date.toLocaleString();
};

// === EMAIL ===

export const getAdminEmailUnreadTotal = (mailboxes: AdminEmailMailboxCount[]): number | null => {
  if (mailboxes.some((mailbox) => mailbox.unreadCount === null || mailbox.error)) {
    return null;
  }

  return mailboxes.reduce((total, mailbox) => total + (mailbox.unreadCount ?? 0), 0);
};
